import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { FilePlus, Check } from "lucide-react";
import { Panel, Inp, Btn, Lbl, RT } from "./ui";

export default function SectionSupplements({ rep, user, onChanged }) {
  const [form, setForm] = useState(null);
  const isSup = ["supervisor", "watch_commander", "records_supervisor", "admin"].includes(user?.role);
  const canFile = ["APPROVED", "LOCKED", "SUPPLEMENT REQUIRED", "ARCHIVED"].includes(rep.status);
  const submit = async () => {
    try {
      await client.post(`/rms-reports/${rep.id}/supplements`, form);
      toast.success("Supplemental report filed → supervisor approval");
      setForm(null); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Supplement failed"); }
  };
  const approve = async (snum) => {
    try { await client.post(`/rms-reports/${rep.id}/supplements/${snum}/approve`); toast.success("Supplement approved"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
  };
  const rows = rep.supplements || [];
  return (
    <Panel title={`Supplemental Reports (${rows.length})`} testid="panel-supplements"
      right={canFile && <Btn small onClick={() => setForm({ reason: "", narrative: "", unit: "" })} testid="supp-new-btn"><FilePlus size={11} /> New Supplement</Btn>}>
      <div className="font-mono text-[9px] mb-2" style={{ color: RT.dim }}>
        The approved original narrative is never overwritten. New information after approval is filed as a numbered supplemental report.
      </div>
      {rows.length === 0 && <div className="font-mono text-[11px] py-3 text-center" style={{ color: RT.dim }}>NO SUPPLEMENTAL REPORTS</div>}
      {rows.map((s) => (
        <div key={s.snum} className="border rounded-sm p-2.5 mb-2" style={{ borderColor: RT.border }} data-testid={`supp-${s.snum}`}>
          <div className="flex items-center gap-2 font-mono text-[11px]">
            <span className="font-bold" style={{ color: RT.head }}>{s.snum}</span>
            <span style={{ color: s.status === "APPROVED" ? "#15803d" : "#b45309" }}>{s.status}</span>
            <span style={{ color: RT.dim }}>{s.rank} {s.author} · Unit {s.unit || "—"} · {s.ts?.slice(0, 16)}</span>
            {isSup && s.status === "PENDING" && (
              <Btn small onClick={() => approve(s.snum)} color="#15803d" testid={`supp-approve-${s.snum}`}><Check size={10} /> Approve</Btn>
            )}
          </div>
          <div className="font-mono text-[10px] mt-1" style={{ color: RT.dim }}>REASON: {s.reason}</div>
          <div className="text-[12px] mt-1 whitespace-pre-wrap" style={{ color: RT.text }}>{s.narrative}</div>
          {s.approved_by && <div className="font-mono text-[9px] mt-1" style={{ color: RT.dim }}>APPROVED BY {s.approved_by} · {s.approved_at?.slice(0, 16)}</div>}
        </div>
      ))}
      {form && (
        <div className="border rounded-sm p-3" style={{ borderColor: RT.head, background: "#eef4f9" }} data-testid="supp-form">
          <div className="grid grid-cols-2 gap-2 mb-2">
            <Inp label="Reason for Supplement *" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} testid="supp-reason" />
            <Inp label="Unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} testid="supp-unit" />
          </div>
          <Lbl>Supplement Narrative *</Lbl>
          <textarea data-testid="supp-narrative" value={form.narrative} rows={5}
            onChange={(e) => setForm({ ...form, narrative: e.target.value })}
            className="w-full border px-2 py-1.5 text-[12px] rounded-sm outline-none"
            style={{ background: "#fff", borderColor: RT.border, color: RT.text }} />
          <div className="flex gap-2 mt-2">
            <Btn onClick={submit} testid="supp-submit">File Supplement</Btn>
            <Btn onClick={() => setForm(null)} color="#64748b" testid="supp-cancel">Cancel</Btn>
          </div>
        </div>
      )}
    </Panel>
  );
}
